import PerfectScrollbar from "perfect-scrollbar";

import { Factory } from "@/data/dao/Factory";
import { type IOfflineStorage, OfflineStorage } from "@/data/dao/offline/OfflineStorage";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { Language } from "@/data/model/Enums";
import type { ILocation } from "@/data/model/Location";
import { Format } from "@/data/util/Format";

import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { portalEvents } from "@/portal/events/portalEvents";
import { getLocationName } from "@/shared/helpers/LocationHelper";

export class LocationSwitch {
    private $element?: JQuery;
    private $current?: JQuery;
    private $options?: JQuery;
    private $inner?: JQuery;
    private perfectScrollBar: any;

    private $searchForm?: JQuery;
    private $searchField?: JQuery;

    private offlineStorage: IOfflineStorage;
    private currentLocation?: ILocation;
    private searchDelay?: number;

    public static currentLocationCacheKey = "currentLocation";
    private static lastLocationsCacheKey = "lastLocations";

    constructor($element?: JQuery) {
        if ($element !== undefined) {
            this.$element = $element;

            this.$options = this.$element.find("> .switchoptions");
            this.$inner = this.$options.find("> .inner");

            this.$searchForm = this.$options.find("> .kt-quick-search__form");
            this.$searchForm.submit((e) => {
                e.preventDefault();
                e.stopPropagation();
                const text = this.$searchField!.val();
                if (text.length > 0) this.onSearch(text);

                return false;
            });

            this.$searchField = this.$searchForm.find("input.form-control");
            this.$searchField.keyup(() => {
                if (this.searchDelay !== undefined) clearTimeout(this.searchDelay);

                const text = this.$searchField!.val();
                if (text.length > 1) {
                    this.searchDelay = window.setTimeout(() => {
                        this.onSearch(text);
                        this.searchDelay = undefined;
                    }, 300);
                }
            });

            this.$current = this.$element.find("> .current");
            this.$current.click((e) => {
                e.preventDefault();

                if (this.$element!.hasClass("shown")) this.close();
                else this.open();
            });
        }

        this.offlineStorage = Factory.getInstance<IOfflineStorage>(OfflineStorage);
        this.initDefaultLocation().then(() => {
            if ($element !== undefined) this.setLastFound();
        });
    }

    private open() {
        this.$options!.show();
        this.$element!.addClass("shown");

        $("html, body").bind("click.locationswitch", (e) => {
            e.preventDefault();

            if ($(e.target).is(this.$element) || $(e.target).parents(".locationswitch").length > 0) {
                e.stopPropagation();
                return;
            }

            this.close();
        });
    }

    private close() {
        this.$options!.hide();
        this.$element!.removeClass("shown");
        $("html, body").unbind("click.locationswitch");
        this.setLastFound();
    }

    private initDefaultLocation(): Promise<void> {
        return new Promise((resolve) => {
            this.offlineStorage = Factory.getInstance<IOfflineStorage>(OfflineStorage);
            const selectedLocation = this.offlineStorage.read(LocationSwitch.currentLocationCacheKey);
            if (selectedLocation !== undefined && selectedLocation != null && selectedLocation["id"] !== undefined) {
                this.currentLocation = selectedLocation;

                if (this.$current !== undefined)
                    this.$current.html(
                        '<div class="locationname"><i class="la la-cog"></i>' +
                            Format.htmlEscape(getLocationName(selectedLocation)) +
                            "</div>"
                    );

                resolve();
                return;
            }

            if (this.$current !== undefined)
                this.$current.html(
                    '<div class="locationname"><i class="la la-cog"></i>' +
                        i18n("general.locationSwitch.pleaseSelect") +
                        "</div>"
                );

            //load 4 locations
            return Factory.getInstance<LocationDao>(LocationDao)
                .getData({
                    limit: 4,
                })
                .then((locations) => {
                    if (locations.items.length === 0) {
                        resolve();
                        return;
                    }

                    this.currentLocation = locations.items[0];
                    this.offlineStorage.write(LocationSwitch.currentLocationCacheKey, this.currentLocation);
                    this.offlineStorage.write(LocationSwitch.lastLocationsCacheKey, locations.items);

                    if (this.$current !== undefined)
                        this.$current.html(
                            '<div class="locationname"><i class="la la-cog"></i>' +
                                Format.htmlEscape(this.currentLocation.name) +
                                "</div>"
                        );

                    resolve();
                    portalEvents.OnLocationChanged.dispatch(this.currentLocation.id);
                })
                .catch((ex) => {
                    logError(ex);
                    resolve();
                });
        });
    }

    private setLastFound() {
        this.$searchForm!.find("> .input-group > .input-group-append > .input-group-text").unbind("click").empty();
        this.$searchField!.val("");

        if (this.searchDelay !== undefined) {
            clearTimeout(this.searchDelay);
            this.searchDelay = undefined;
        }

        if (this.perfectScrollBar !== undefined) {
            this.perfectScrollBar.destroy();
            this.perfectScrollBar = undefined;
        }

        this.$inner!.empty();
        const lastFoundLocations = this.offlineStorage.read(LocationSwitch.lastLocationsCacheKey);
        if (lastFoundLocations == null || lastFoundLocations === undefined) return;

        const searchResults = $('<div class="searchresults" />').appendTo(this.$inner!);
        searchResults.html(
            '<div class="font-size-sm text-primary font-weight-bolder text-uppercase mb-2">' +
                i18n("general.locationSwitch.lastLocations") +
                "</div>"
        );
        for (const i in lastFoundLocations) searchResults.append(this.renderLocation(lastFoundLocations[i]));
    }

    private onSearch(term: string) {
        const loaderContainer = this.$searchForm!.find("> .input-group > .input-group-append > .input-group-text");
        loaderContainer.unbind("click").html('<div class="kt-spinner kt-spinner--sm kt-spinner--brand"></div>');

        Factory.getInstance<LocationDao>(LocationDao)
            .getData({
                term: term,
                limit: term.length <= 3 ? 15 : 30,
            })
            .then((locations) => {
                if (this.perfectScrollBar !== undefined) {
                    this.perfectScrollBar.destroy();
                    this.perfectScrollBar = undefined;
                }

                const searchResults = $('<div class="searchresults" />').appendTo(this.$inner!.empty());
                searchResults.html(
                    `<div class="font-size-sm text-primary font-weight-bolder text-uppercase mb-2">${i18n("general.locationSwitch.searchResults")}</div>`
                );
                const resultInner = $(
                    '<div class="kt-scroll ps" data-scroll="true" data-height="300" style="max-height:300px;" />'
                ).appendTo(searchResults);
                if (locations.items.length === 0) {
                    resultInner.append(
                        `<div class="nodata">${i18n("general.locationSwitch.noLocationsFound")}</div>`
                    );
                } else {
                    for (const i in locations.items) resultInner.append(this.renderLocation(locations.items[i]));
                }

                this.perfectScrollBar = new PerfectScrollbar(resultInner[0], {
                    wheelSpeed: 1,
                    swipeEasing: true,
                    wheelPropagation: true,
                    minScrollbarLength: 40,
                    maxScrollbarLength: 300,
                });

                loaderContainer.html('<i class="la la-close kt-quick-search__close"></i>').bind("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    this.setLastFound();
                });
            });
    }

    private renderLocation(res: ILocation): JQuery {
        const c = $('<div class="location"><span class="name">' + Format.htmlEscape(getLocationName(res)) + "</span></div>").data(
            "model",
            res
        );
        c.prepend(
            '<span class="icon"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="24px" height="24px" viewBox="0 0 24 24" version="1.1" class="kt-svg-icon"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><rect x="0" y="0" width="24" height="24"></rect><path d="M13.5,21 L13.5,18 C13.5,17.4477153 13.0522847,17 12.5,17 L11.5,17 C10.9477153,17 10.5,17.4477153 10.5,18 L10.5,21 L5,21 L5,4 C5,2.8954305 5.8954305,2 7,2 L17,2 C18.1045695,2 19,2.8954305 19,4 L19,21 L13.5,21 Z M9,4 C8.44771525,4 8,4.44771525 8,5 L8,6 C8,6.55228475 8.44771525,7 9,7 L10,7 C10.5522847,7 11,6.55228475 11,6 L11,5 C11,4.44771525 10.5522847,4 10,4 L9,4 Z M14,4 C13.4477153,4 13,4.44771525 13,5 L13,6 C13,6.55228475 13.4477153,7 14,7 L15,7 C15.5522847,7 16,6.55228475 16,6 L16,5 C16,4.44771525 15.5522847,4 15,4 L14,4 Z M9,8 C8.44771525,8 8,8.44771525 8,9 L8,10 C8,10.5522847 8.44771525,11 9,11 L10,11 C10.5522847,11 11,10.5522847 11,10 L11,9 C11,8.44771525 10.5522847,8 10,8 L9,8 Z M9,12 C8.44771525,12 8,12.4477153 8,13 L8,14 C8,14.5522847 8.44771525,15 9,15 L10,15 C10.5522847,15 11,14.5522847 11,14 L11,13 C11,12.4477153 10.5522847,12 10,12 L9,12 Z M14,12 C13.4477153,12 13,12.4477153 13,13 L13,14 C13,14.5522847 13.4477153,15 14,15 L15,15 C15.5522847,15 16,14.5522847 16,14 L16,13 C16,12.4477153 15.5522847,12 15,12 L14,12 Z" fill="#000000"></path><rect fill="#FFFFFF" x="13" y="8" width="3" height="3" rx="1"></rect><path d="M4,21 L20,21 C20.5522847,21 21,21.4477153 21,22 L21,22.4 C21,22.7313708 20.7313708,23 20.4,23 L3.6,23 C3.26862915,23 3,22.7313708 3,22.4 L3,22 C3,21.4477153 3.44771525,21 4,21 Z" fill="#000000" opacity="0.3"></path></g></svg></span>'
        );
        c.click((e) => {
            this.onClickLocation($(e.currentTarget).data("model"));
            this.close();
        });

        if (this.currentLocation !== undefined && res.id === this.currentLocation.id) c.addClass("active");

        return c;
    }

    private onClickLocation(r: ILocation) {
        const lastFoundLocations: any[] = [];
        const cfg = this.offlineStorage.read(LocationSwitch.lastLocationsCacheKey);
        if (cfg != null && cfg !== undefined) {
            for (const i in cfg) lastFoundLocations.push(cfg[i]);
        }

        //make sure to not insert same location twice
        const currentEl = lastFoundLocations.find((c) => c.id === r.id);
        if (currentEl !== undefined) {
            const currentIndex = lastFoundLocations.indexOf(currentEl);
            lastFoundLocations.splice(0, 0, lastFoundLocations.splice(currentIndex, 1)[0]);
        } else {
            if (lastFoundLocations.length > 4) lastFoundLocations.splice(lastFoundLocations.length - 1, 1);

            lastFoundLocations.unshift(r);
        }

        this.currentLocation = r;
        this.offlineStorage.write(LocationSwitch.currentLocationCacheKey, this.currentLocation);
        this.offlineStorage.write(LocationSwitch.lastLocationsCacheKey, lastFoundLocations);
        this.$current!.html(
            '<div class="locationname"><i class="la la-cog"></i>' + Format.htmlEscape(getLocationName(r)) + "</div>"
        );
        portalEvents.OnLocationChanged.dispatch(r.id);
    }

    public getCurrentLocationId(): string | undefined {
        if (this.currentLocation === undefined) return undefined;

        return this.currentLocation.id;
    }

    public getCurrentLocationName(): string | undefined {
        if (this.currentLocation == null) 
            return undefined;

        return getLocationName(this.currentLocation);
    }

    public getCurrentLocationLanguage(): Language {
        if (this.currentLocation === undefined) return Language.English;

        return this.currentLocation.language;
    }

    public setCurrentLocation(name: string, id: string, language: Language) {
        this.currentLocation = {
            id: id,
            name: name,
            internalName: undefined,
            created: undefined!,
            state: undefined!,
            language: language,
            childLocations: [],
        };
    }

    public static releaseCache() {
        const storage = Factory.getInstance<IOfflineStorage>(OfflineStorage);
        storage.remove(LocationSwitch.currentLocationCacheKey);
        storage.remove(LocationSwitch.lastLocationsCacheKey);
    }
}
